import Typography from "@material-ui/core/Typography";
import socketIOClient from "socket.io-client";
import React, { FC, useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import CustomDomainListCreateButton from "./components/CustomDomainListCreateButton";
import CustomDomainListsDashboard from "./components/CustomDomainListsDashboard";
import CustomSplitDomainListsDashboard from "./components/CustomSplitDomainListsDashboard";
import PublicDomainListsDashboard from "./components/PublicDomainListsDashboard";
import PublicSplitDomainListsDashboard from "./components/PublicSplitDomainListsDashboard";
import api from "../../../api";
import classes from "./PublicationListSelectionSlide.module.scss";
import { APIResponse } from "../../../types/APIResponseType";
import { CAMPAIGN_STEPS, CustomDomainList, Domain, PublicDomainListData } from "../../../types/campaignTypes";
import { PageStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { basicSteps } from "../../../constants/steps";
import { errorToaster } from "../../../helpers/toasters";
import {
  setCampaignToCreateSelectedAdvertiser,
  setCustomDomainLists,
  setPublicDomainLists,
  setSelectedDomainLists as setSelectedPublicDomainLists,
  setSelectedDomainListsIds,
} from "../../../redux/reducers/campaignToCreate";

const getPublicDomainLists = async () => {
  try {
    const { payload: publicDomainLists } = await api.getPublicPublicationLists();
    return publicDomainLists;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const getCustomDomainLists = async (brandgenAdvertiserId: string) => {
  try {
    const { payload: customDomainLists } = await api.getDomainLists(brandgenAdvertiserId);
    return customDomainLists;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const createDomainList = async (
  bgAdvertiserId: string,
  name: string,
  domains: string[],
  saveCreatedDomainList: (createdDomainList: CustomDomainList) => void,
) => {
  const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);
  const body = {
    bgAdvertiserId,
    listName: name,
    domains,
  };
  socket.emit("createNewDomainList", body);

  socket.on("newDomainListCreated", (response: APIResponse) => {
    // set created domain list
    saveCreatedDomainList(response.payload as CustomDomainList);
    socket.close();
  });

  socket.on("domainListCreationError", (domainListCreationError: any) => {
    socket.close();
    throw new Error(domainListCreationError);
  });
};

const updateDomainList = async (
  bgAdvertiserId: string,
  beeswaxListId: number,
  domainListId: string,
  listName: string,
  domainsToUpdate: Domain[],
  domainsToAdd: string[],
  saveUpdatedDomainListToStore: (updatedDomainLists: CustomDomainList[]) => void,
) => {
  const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);
  const body = {
    bgAdvertiserId,
    beeswaxListId,
    domainListId,
    listName,
    domainsToUpdate,
    domainsToAdd,
  };
  socket.emit("updateDomainList", body);

  socket.on("domainListUpdated", (response: APIResponse) => {
    saveUpdatedDomainListToStore(response.payload as CustomDomainList[]);
    socket.close();
  });

  socket.on("domainListUpdateError", (domainListUpdateError: any) => {
    socket.close();
    throw new Error(domainListUpdateError);
  });
};

const PublicationListSelectionSlide: FC<PageStepComponentProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}) => {
  const brandgenAdvertiserId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId,
  )!;
  const selectedDomainListsIdsFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainListsIds,
  );
  const selectedDomainListsFromStore = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedDomainLists,
  );
  const publicDomainLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.publicDomainLists);
  const customDomainLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.customDomainLists);

  const [selectedDomainListsIds, setSelectedDomainListssIds] = useState<number[]>(selectedDomainListsIdsFromStore);
  const [selectedDomainLists, setSelectedDomainLists] = useState<PublicDomainListData[]>(selectedDomainListsFromStore);

  const [selectedDomainListsGroupIds, setSelectedDomainListsGroupIds] = useState<string[]>([]);

  const [isLoadingDomainLists, setIsLoadingDomainLists] = useState(false);

  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState("");
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);

  const campaignSteps = useSelector((state: ReduxApplicationState) => state.campaignToCreate.campaignSteps);

  const isOnlyPublicationListsSelected = (steps?: CAMPAIGN_STEPS[] | null): boolean => {
    if (!steps || steps.length === 0) return false;

    const nonBaseSteps = steps.filter((step) => !basicSteps.includes(step));

    return nonBaseSteps.length === 1 && nonBaseSteps[0] === CAMPAIGN_STEPS.PUBLICATION_LISTS;
  };

  const onlyPublicationListsSelected = React.useMemo(() => {
    return isOnlyPublicationListsSelected(campaignSteps);
  }, [campaignSteps]);

  const dispatch = useDispatch();

  const toggleSelectDomainLists = (toggledBeesWaxListId: number) => {
    if (selectedDomainListsIds.includes(toggledBeesWaxListId)) {
      setSelectedDomainListssIds(
        selectedDomainListsIds.filter((beesWaxListId) => beesWaxListId !== toggledBeesWaxListId),
      );
    } else {
      setSelectedDomainListssIds([...selectedDomainListsIds, toggledBeesWaxListId]);
    }
  };

  const toggleSelectPublicSplitDomainLists = (
    payload: PublicDomainListData | PublicDomainListData[],
    include: boolean = true,
  ) => {
    const toggled = Array.isArray(payload) ? payload : [payload];
    const toggledIds = toggled.map((item) => item.beeswaxListId);

    let newSelectedIds: number[];
    let newSelectedObjects: PublicDomainListData[];

    if (!include) {
      newSelectedIds = selectedDomainListsIds.filter((id) => !toggledIds.includes(id));
      newSelectedObjects = selectedDomainLists.filter((item) => !toggledIds.includes(item.beeswaxListId));
    } else {
      newSelectedIds = [...new Set([...selectedDomainListsIds, ...toggledIds])];

      newSelectedObjects = [
        ...selectedDomainLists.filter((item) => !toggledIds.includes(item.beeswaxListId)),
        ...toggled,
      ];
    }

    setSelectedDomainListssIds(newSelectedIds);
    setSelectedDomainLists(newSelectedObjects);
  };

  const toggleSelectDomainListGroups = (toggledListGroupId: string) => {
    if (selectedDomainListsGroupIds.includes(toggledListGroupId)) {
      setSelectedDomainListsGroupIds(
        selectedDomainListsGroupIds.filter((listGroupId) => listGroupId !== toggledListGroupId),
      );
    } else {
      setSelectedDomainListsGroupIds([...selectedDomainListsGroupIds, toggledListGroupId]);
    }
  };

  useEffect(() => {
    const getDomainListsAndSetState = async () => {
      try {
        setIsLoadingDomainLists(true);
        const fetchedPublicDomainList = await getPublicDomainLists();
        const fetchedCustomDomainList = await getCustomDomainLists(brandgenAdvertiserId);
        dispatch(setPublicDomainLists(fetchedPublicDomainList));
        dispatch(setCustomDomainLists(fetchedCustomDomainList));
        setIsLoadingDomainLists(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("getDomainListsAndSetState error: ", error);
        setIsLoadingDomainLists(false);
      }
    };

    if (publicDomainLists.length === 0) {
      getDomainListsAndSetState();
    }
  }, [brandgenAdvertiserId, dispatch]);

  useEffect(() => {
    if (isAttemptingToGoNext && setIsAttemptingToGoNext && handleNext) {
      if (selectedDomainListsIds.length !== 0) {
        dispatch(setSelectedDomainListsIds(selectedDomainListsIds));
        dispatch(setSelectedPublicDomainLists(selectedDomainLists));
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, select required domain lists.", 5000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [dispatch, handleNext, isAttemptingToGoNext, selectedDomainListsIds, setIsAttemptingToGoNext]);

  useEffect(() => {
    if (!selectedAdvertiserId && setIsAttemptingToGoNext) return;

    const selectedAdvertiser = advertisers.find(
      (advertiser) => advertiser.brandgenAdvertiserId === selectedAdvertiserId,
    );

    if (selectedAdvertiser) dispatch(setCampaignToCreateSelectedAdvertiser(selectedAdvertiser));

    const getCustomDomainListsAndSetState = async () => {
      try {
        setIsLoadingDomainLists(true);
        const fetchedCustomDomainList = await getCustomDomainLists(selectedAdvertiserId);
        dispatch(setCustomDomainLists(fetchedCustomDomainList));
        setIsLoadingDomainLists(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("getCustomDomainListsAndSetState error: ", error);
        setIsLoadingDomainLists(false);
      }
    };
    if (selectedAdvertiserId) {
      getCustomDomainListsAndSetState();
    }
  }, [advertisers, dispatch, selectedAdvertiserId, setIsAttemptingToGoNext]);

  const mainContent = () => (
    <>
      {!onlyPublicationListsSelected && (
        <CustomDomainListCreateButton isLoading={isLoadingDomainLists} createDomainList={createDomainList} />
      )}
      {isLoadingDomainLists ? (
        <Typography className={classes.loaderLabel}>Please, wait while we get everything in order...</Typography>
      ) : null}
      <div className={classes.container}>
        {!isLoadingDomainLists && publicDomainLists.length !== 0 ? (
          !onlyPublicationListsSelected ? (
            <>
              <PublicDomainListsDashboard
                publicDomainLists={publicDomainLists}
                selectedDomainListsIds={selectedDomainListsIds}
                toggleSelectDomainLists={toggleSelectDomainLists}
              />
              <CustomDomainListsDashboard
                customDomainLists={customDomainLists}
                selectedDomainListsIds={selectedDomainListsIds}
                toggleSelectDomainLists={toggleSelectDomainLists}
                updateDomainList={updateDomainList}
              />
            </>
          ) : (
            <>
              <PublicSplitDomainListsDashboard
                publicDomainLists={publicDomainLists}
                selectedDomainListsIds={selectedDomainListsIds}
                toggleSelectDomainLists={toggleSelectPublicSplitDomainLists}
                toggleSelectDomainListGroups={toggleSelectDomainListGroups}
                selectedDomainListsGroupIds={selectedDomainListsGroupIds}
              />
              <CustomSplitDomainListsDashboard
                customDomainLists={customDomainLists}
                selectedDomainListsIds={selectedDomainListsIds}
                toggleSelectDomainLists={toggleSelectPublicSplitDomainLists}
                toggleSelectDomainListGroups={toggleSelectDomainListGroups}
                selectedDomainListsGroupIds={selectedDomainListsGroupIds}
              />
            </>
          )
        ) : null}
      </div>
    </>
  );

  return (
    <div
      style={{
        padding: !setIsAttemptingToGoNext ? "4rem" : 0,
      }}
    >
      {!setIsAttemptingToGoNext && (
        <div
          style={{
            margin: "3rem 0",
            width: "20rem",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Select Advertiser</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedAdvertiserId}
              label="Advertiser"
              onChange={(e) => {
                setSelectedAdvertiserId(e.target.value as string);
              }}
            >
              {advertisers.map((advertiser) => {
                return (
                  <MenuItem key={advertiser.brandgenAdvertiserId} value={advertiser.brandgenAdvertiserId}>
                    {advertiser.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      )}
      {setIsAttemptingToGoNext ? (
        <CampaignCreationSlideContainer
          heading={`Select ${!onlyPublicationListsSelected ? "or Create" : ""} Publication Lists:`}
        >
          {mainContent()}
        </CampaignCreationSlideContainer>
      ) : (
        mainContent()
      )}
    </div>
  );
};

export default PublicationListSelectionSlide;
